import { useState, useEffect } from "react";
import {
  Activity, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Link2, Unlink, Clock, Database, Wifi, WifiOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
      Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-500/10 text-red-400 border border-red-500/20">
      <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
      Disconnected
    </span>
  );
}

export default function SyncHealth({ connection, onSync, onReconnect, onRefresh }) {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/sync-status`, { headers });
        if (!res.ok) return;
        setSyncStatus(await res.json());
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync();
      // Poll for completion
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 2000));
        const res = await fetch(`${API}/quickbooks/sync-status`, { headers });
        if (res.ok) {
          const data = await res.json();
          setSyncStatus(data);
          if (data.last_sync) break;
        }
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Are you sure you want to disconnect QuickBooks?")) return;
    setDisconnecting(true);
    try {
      await fetch(`${API}/quickbooks/disconnect`, { method: "POST", headers });
      onRefresh();
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="sync-health">
      {/* Connection Status */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-base font-medium text-white mb-1">QuickBooks Connection</h3>
            <p className="text-sm text-white/40">
              {connection?.company_name || "Not connected"}
              {connection?.environment && (
                <span className="ml-2 text-[10px] bg-white/[0.06] px-1.5 py-0.5 rounded">{connection.environment}</span>
              )}
            </p>
          </div>
          <StatusBadge connected={connection?.connected} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Realm ID</p>
            <p className="text-xs text-white/60 font-mono">{syncStatus?.realm_id || "-"}</p>
          </div>
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Last Sync</p>
            <p className="text-xs text-white/60">
              {syncStatus?.last_sync ? new Date(syncStatus.last_sync).toLocaleString() : "Never"}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Sync Duration</p>
            <p className="text-xs text-white/60">
              {syncStatus?.sync_duration_seconds ? `${syncStatus.sync_duration_seconds.toFixed(1)}s` : "-"}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Last Webhook</p>
            <p className="text-xs text-white/60">
              {syncStatus?.last_webhook?.received_at ? new Date(syncStatus.last_webhook.received_at).toLocaleString() : "None"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Access Token Expires</p>
            <p className="text-xs text-white/60">
              {syncStatus?.access_token_expires ? new Date(syncStatus.access_token_expires).toLocaleString() : "-"}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-white/30 uppercase tracking-wider mb-1">Refresh Token Expires</p>
            <p className="text-xs text-white/60">
              {syncStatus?.refresh_token_expires ? new Date(syncStatus.refresh_token_expires).toLocaleString() : "-"}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            data-testid="force-sync-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-white/[0.06] border border-white/[0.1] rounded-lg hover:bg-white/[0.1] transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Force Sync Now"}
          </button>
          <button
            onClick={onReconnect}
            data-testid="reconnect-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white/70 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:bg-white/[0.06] transition-all"
          >
            <Link2 className="w-3.5 h-3.5" />
            Reconnect
          </button>
          <button
            onClick={handleDisconnect}
            disabled={disconnecting}
            data-testid="disconnect-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-red-400/70 bg-red-500/[0.04] border border-red-500/10 rounded-lg hover:bg-red-500/[0.08] transition-all disabled:opacity-50 ml-auto"
          >
            <Unlink className="w-3.5 h-3.5" />
            Disconnect
          </button>
        </div>
      </div>

      {/* Record Counts */}
      {syncStatus?.record_counts && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
          <h3 className="text-sm font-medium text-white/60 mb-4 uppercase tracking-wider flex items-center gap-2">
            <Database className="w-4 h-4" />
            Synced Data
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(syncStatus.record_counts).map(([key, count]) => (
              <div key={key} className="text-center py-3 bg-white/[0.02] rounded-lg border border-white/[0.04]">
                <p className="text-xl font-light text-white/80">{count}</p>
                <p className="text-[10px] text-white/40 mt-0.5 capitalize">{key}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync Errors */}
      {syncStatus?.sync_errors?.length > 0 && (
        <div className="bg-red-500/[0.04] border border-red-500/10 rounded-xl p-6">
          <h3 className="text-sm font-medium text-red-400/80 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Sync Errors
          </h3>
          <div className="space-y-1.5">
            {syncStatus.sync_errors.map((err, i) => (
              <p key={i} className="text-xs text-red-400/60 font-mono bg-red-500/[0.04] px-3 py-1.5 rounded">
                {err}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
